/**
 * Déclencheurs de SMS créés par l'utilisateur (Messagerie › Réglages des
 * relances › « Créer un déclencheur »).
 *
 * Un déclencheur = un événement (devis envoyé, facture émise, début ou fin
 * de chantier), un délai en jours, un message. Le moteur `relances-run`
 * (2 passages par jour) envoie le message une seule fois par devis, facture
 * ou chantier. Il recopie ces règles ; elles sont testées ici.
 *
 * Fonctions PURES : sans base ni réseau.
 */

export type EvenementPerso = 'devis_envoye' | 'facture_emise' | 'chantier_debut' | 'chantier_fin';

export interface DeclencheurPerso {
  /** Identifiant court, stable : il sert de palier d'idempotence. */
  id: string;
  nom: string;
  actif: boolean;
  evenement: EvenementPerso;
  /** Jours après l'événement (ou avant, pour le début de chantier). */
  delai: number;
  message: string;
}

// Les messages types restent dans l'alphabet GSM (apostrophe droite, pas de
// tiret long) et courts : un seul caractère hors alphabet fait passer tout le
// SMS dans un encodage où il coûte deux à trois fois plus.
export interface DefinitionEvenement {
  label: string;
  /** « après » : on compte depuis l'événement ; « avant » : jusqu'à lui. */
  sens: 'apres' | 'avant';
  variables: string;
  modele: string;
}

export const EVENEMENTS_PERSO: Record<EvenementPerso, DefinitionEvenement> = {
  devis_envoye: {
    label: 'Devis envoyé',
    sens: 'apres',
    variables: '{prenom} {numero} {montant}',
    modele:
      'Bonjour {prenom}, IMP TOITURE. Notre devis {numero} vous est-il bien parvenu par e-mail ? Sinon, regardez vos spams ou répondez à ce SMS. STOP pour vous désinscrire.',
  },
  facture_emise: {
    label: 'Facture émise',
    sens: 'apres',
    variables: '{prenom} {numero} {montant}',
    modele:
      'Bonjour {prenom}, IMP TOITURE. Votre facture {numero} de {montant} € vous a été envoyée par e-mail. Une question ? Répondez à ce SMS.',
  },
  chantier_debut: {
    label: 'Début de chantier',
    sens: 'avant',
    variables: '{prenom} {date} {ville}',
    modele:
      "Bonjour {prenom}, IMP TOITURE. Nous intervenons le {date} à {ville}. En cas d'imprévu, merci de répondre à ce SMS.",
  },
  chantier_fin: {
    label: 'Chantier terminé',
    sens: 'apres',
    variables: '{prenom} {ville}',
    modele:
      'Bonjour {prenom}, IMP TOITURE. Votre chantier à {ville} est terminé. Merci de votre confiance ! Une remarque ? Répondez à ce SMS.',
  },
};

/**
 * Le message est-il dû aujourd'hui ? On tolère deux jours de retard (un
 * passage du moteur manqué, un week-end hors créneau), pas davantage : créer
 * un déclencheur ne doit pas écrire à tous les clients des mois passés.
 *
 * `ecartJours` : jours écoulés depuis l'événement (sens « après ») ou jours
 * restant avant lui (sens « avant »).
 */
export function estDu(sens: 'apres' | 'avant', delai: number, ecartJours: number): boolean {
  if (!Number.isFinite(ecartJours) || delai < 0) return false;
  if (sens === 'apres') return ecartJours >= delai && ecartJours <= delai + 2;
  return ecartJours <= delai && ecartJours >= Math.max(0, delai - 2);
}

/** Le palier d'idempotence (24 caractères au plus en base). */
export function palierPerso(id: string): string {
  return `p:${id}`.slice(0, 24);
}

/** Un nouveau déclencheur, prêt à modifier : confirmer la réception d'un devis. */
export function nouveauDeclencheur(id: string): DeclencheurPerso {
  return {
    id,
    nom: 'Confirmation de réception du devis',
    actif: true,
    evenement: 'devis_envoye',
    delai: 1,
    message: EVENEMENTS_PERSO.devis_envoye.modele,
  };
}

/** Ce qui empêche d'enregistrer ce déclencheur, ou `null`. */
export function erreurDeclencheur(d: DeclencheurPerso): string | null {
  if (d.nom.trim() === '') return 'Donnez un nom au déclencheur.';
  if (!Number.isInteger(d.delai) || d.delai < 0 || d.delai > 365) {
    return `« ${d.nom} » : le délai doit être un nombre de jours entre 0 et 365.`;
  }
  if (d.message.trim() === '') return `« ${d.nom} » : le message est vide.`;
  return null;
}
